/**
 * Sectioned, spec-driven editor for bot default config. Reused by the org
 * defaults panel (admin) and the personal defaults panel (trader).
 * Deliberately NOT a flat grid: fields are grouped by meaning per
 * BOT_KIND_SPECS, and `orgOnly` (strategy) sections are hidden from the
 * personal scope. Blank inputs mean "inherit the next layer" (personal → org →
 * edge built-in), so every value is optional.
 *
 * Used by exactly those two panels. (A docstring here once claimed a third
 * consumer, the dashboard's edge-spawn form; that claim was untrue when written
 * — #312 — and the spawn form itself was removed with the g10edge lane.)
 *
 * Values are checked here before they reach the parent, so a save request can
 * never carry a magnitude the stored contract rejects (#312). The relay remains
 * the final authority — this is inline feedback, not the enforcement boundary.
 */
import { useEffect, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { BOT_KIND_SPECS, parseKnobInput, type BotField } from '@/lib/bots/configSpec';
import type { BotKindConfig } from '@/lib/bots/botDefaults';

type ByKind = Partial<Record<string, BotKindConfig>>;

/** Shallow-clone a byKind map so edits don't mutate the source (e.g. the store). */
export function cloneByKind(src: ByKind | undefined): ByKind {
  const out: ByKind = {};
  for (const [k, v] of Object.entries(src ?? {})) out[k] = { ...v };
  return out;
}

export function BotDefaultsEditor({
  byKind,
  scope,
  onChange,
  onValidityChange,
}: {
  byKind: ByKind;
  scope: 'org' | 'personal';
  onChange: (next: ByKind) => void;
  /** Fires when the editor gains or loses invalid input, so the owning panel
   *  can block Save. Without it a valid edit elsewhere would leave Save enabled
   *  while a rejected value sits on screen — saving something the trader can
   *  see is not what they asked for. */
  onValidityChange?: (valid: boolean) => void;
}) {
  // The raw text of every field the trader is currently editing, keyed
  // `${kind}:${key}`. The editor renders this in preference to the stored
  // number for two reasons: a rejected value must stay on screen next to its
  // error rather than silently snapping back, and an accepted value must not be
  // re-normalized mid-keystroke (storing "2." as 2 would re-render "2" and eat
  // the decimal point as the trader types "2.5"). A draft is dropped on blur
  // once it parses, so the canonical stored value is what rests on screen.
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const setField = (kind: string, key: string, raw: string) => {
    const id = `${kind}:${key}`;
    setDrafts((prev) => ({ ...prev, [id]: raw }));

    const parsed = parseKnobInput(raw);
    if (parsed.kind === 'invalid') return; // byKind is left exactly as it was

    const next = cloneByKind(byKind);
    const cfg = (next[kind] ??= {});
    if (parsed.kind === 'clear') {
      delete cfg[key]; // blank → inherit
    } else {
      cfg[key] = parsed.value;
    }
    if (Object.keys(cfg).length === 0) delete next[kind];
    onChange(next);
  };

  /** On blur, a field that parses hands the display back to the stored value. */
  const settleField = (id: string) => {
    setDrafts((prev) => {
      const raw = prev[id];
      if (raw === undefined || parseKnobInput(raw).kind === 'invalid') return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const invalidIds = Object.keys(drafts).filter(
    (id) => parseKnobInput(drafts[id]).kind === 'invalid',
  );
  const valid = invalidIds.length === 0;
  // Reported from an effect rather than the change handler so the editor's
  // contract is self-contained: the parent knows this editor's CURRENT validity
  // from mount, not just after the next keystroke. Reporting from the handler
  // alone happens to be equivalent today — a stale `false` is always masked by
  // the panels' `dirty` flag, since both only move via `onChange` — but that
  // equivalence is a property of the panels, not of this component, and it
  // would break silently if a panel ever enabled Save without `dirty`.
  useEffect(() => {
    onValidityChange?.(valid);
  }, [valid, onValidityChange]);

  const [active, setActive] = useState(BOT_KIND_SPECS[0]?.kind ?? '');
  const spec = BOT_KIND_SPECS.find((k) => k.kind === active) ?? BOT_KIND_SPECS[0];
  const sections = spec.sections.filter((s) => scope === 'org' || !s.orgOnly);
  const cfg = byKind[spec.kind] ?? {};

  return (
    <div>
      {/* One tab per bot kind — keeps the card compact instead of stacking every
          kind's full config into one long scroll. */}
      <div className="border-border mb-3 flex flex-wrap gap-1 border-b">
        {BOT_KIND_SPECS.map((k) => {
          // A rejected value on a hidden tab still blocks Save. Mark the tab, or
          // the disabled button has no visible cause from two clicks away.
          const tabHasInvalid = invalidIds.some((id) => id.startsWith(`${k.kind}:`));
          return (
            <button
              key={k.kind}
              type="button"
              onClick={() => setActive(k.kind)}
              className={cn(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs font-medium transition-colors',
                active === k.kind
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {k.label}
              {tabHasInvalid && (
                <span
                  aria-label={`${k.label} has an invalid value`}
                  className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500"
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="space-y-3">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="text-muted-foreground mb-1 text-[10px] uppercase tracking-wider">
              {section.title}
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {section.fields.map((f) => (
                <FieldInput
                  key={f.key}
                  field={f}
                  value={cfg[f.key]}
                  draft={drafts[`${spec.kind}:${f.key}`]}
                  onChange={(raw) => setField(spec.kind, f.key, raw)}
                  onSettle={() => settleField(`${spec.kind}:${f.key}`)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground mt-3 text-[10px]">
        Blank inherits {scope === 'personal' ? 'the org default, then ' : ''}the engine built-in
        (shown as the placeholder). Traders can still override per spawn.
      </p>
    </div>
  );
}

function FieldInput({
  field,
  value,
  draft,
  onChange,
  onSettle,
}: {
  field: BotField;
  value: number | undefined;
  /** In-progress raw text, when the trader is editing this field. */
  draft?: string;
  onChange: (raw: string) => void;
  onSettle: () => void;
}) {
  const parsed = draft === undefined ? null : parseKnobInput(draft);
  const error = parsed?.kind === 'invalid' ? parsed.message : null;

  return (
    <label className="block" title={field.help}>
      <span className="text-muted-foreground flex items-center gap-1 text-[10px]">
        {field.label}
        {field.unit ? <span className="opacity-60">{field.unit}</span> : null}
      </span>
      {/*
        A TEXT input, not `type="number"`. A number input runs the HTML value
        sanitization algorithm before any handler sees the text, so anything it
        cannot parse arrives as "" — indistinguishable from a deliberate clear.
        That made malformed input silently DELETE the override and inherit
        instead: a successful save of a value nobody chose, with no feedback.
        Measured directly (Chrome 150 and jsdom agree): setting "abc", "5abc",
        "0x10", or "1e999" on a number input all yield value === "".

        Taking the raw text puts one parser — `parseKnobInput` — in charge, so
        the same keystroke behaves identically in the app and in the tests.
        `inputMode="decimal"` keeps the numeric keypad on touch devices. `min`
        is dropped because it is inert on a text input and because the ticket's
        premise is that HTML bounds do not enforce this contract anyway.

        Consequence worth knowing: text the number input used to swallow now
        reaches `Number()`, which accepts more grammars than a decimal literal —
        "0x10" reads as 16 and "1e3" as 1000. Both are finite, non-negative, and
        exactly what the relay would store, and the field re-renders with the
        canonical value on blur, so nothing is saved that the trader cannot see.
      */}
      <input
        type="text"
        inputMode="decimal"
        value={draft ?? value ?? ''}
        aria-invalid={!!error}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onSettle}
        placeholder={field.builtin !== undefined ? String(field.builtin) : '—'}
        className={cn(
          'bg-background text-foreground mt-0.5 w-full rounded border px-1.5 py-1 text-xs focus:outline-none',
          error ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-primary',
        )}
      />
      {error && <span className="mt-0.5 block text-[10px] text-red-500 dark:text-red-400">{error}</span>}
    </label>
  );
}
